import React, { useState } from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client/react/hooks';
import { Link } from 'react-router-dom';

import Image from 'lib/images/image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { displayedName } from 'four-nets/user/utils';
import IconUser from 'four-nets/icons/svg/icon_user-2.svg';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';

import query, { MostPopularQuery, MostPopularQueryVariables } from './most-popular-pb-albums.graphql';
import defaultStyles from './most-popular-pb-albums.scss';

export interface Props {
    styles: typeof defaultStyles;
    defaultDayRange?: number;
}

const MostPopular: React.FC<Props> = React.memo((props: Props) => {
    const { styles, defaultDayRange } = props;

    const [dayRange, setDayRange] = useState(defaultDayRange && [3, 7].includes(defaultDayRange) ? defaultDayRange : 7);
    const { loading, data } = useQuery<MostPopularQuery, MostPopularQueryVariables>(query, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-first',
        variables: {
            first: 5,
            dayRange: dayRange,
        },
    });

    const albums = data && data.mostPopular && data.mostPopular.edges;
    const hasNoPosts = !albums || albums.length <= 0;

    return (
        <div className={styles.wrapper}>
            <div className={styles.title}>
                <Link to='/blogs/most-favorite/' className={styles.link}>
                    <FormattedMessage id='FORUM_POPULAR_ALBUMS' />
                </Link>
                <div>
                    <button
                        className={classnames(styles.dayButton, { [styles.active]: dayRange === 3 })}
                        onClick={() => {
                            setDayRange(3);
                        }}
                    >
                        <FormattedMessage id='%(number)s_DAYS' values={{ number: 3 }} />
                    </button>
                    <button
                        className={classnames(styles.dayButton, { [styles.active]: dayRange === 7 })}
                        onClick={() => {
                            setDayRange(7);
                        }}
                    >
                        <FormattedMessage id='%(number)s_DAYS' values={{ number: 7 }} />
                    </button>
                </div>
            </div>
            {!loading && hasNoPosts ? (
                <div className={styles.noResults}>
                    <FormattedMessage id='NO_RESULTS' />
                </div>
            ) : (
                <LoadingWrapperView loading={loading}>
                    {albums
                        ? albums.map(edge => {
                              if (
                                  !edge ||
                                  !edge.node ||
                                  !edge.node.images ||
                                  !edge.node.images.edges ||
                                  !edge.node.images.edges[0] ||
                                  !edge.node.images.edges[0].node ||
                                  !edge.node.images.edges[0].node.photoFile
                              ) {
                                  return null;
                              }
                              const {
                                  id,
                                  title,
                                  slug,
                                  user,
                                  images: { edges: imageEdges },
                              } = edge.node;

                              const link = `/blog/${user && user.username}/album/${slug}/`;
                              const albumPhotoFile = imageEdges[0]!.node!.photoFile!;

                              return (
                                  <div className={styles.row} key={id}>
                                      <div className={styles.image}>
                                          <a href={link} className={styles.link}>
                                              <Image
                                                  sizes={{ xs: 150 }}
                                                  aspectFill={true}
                                                  srcSet={albumPhotoFile.srcset}
                                                  src={albumPhotoFile.src}
                                                  alt={title}
                                                  linkify={false}
                                              />
                                          </a>
                                      </div>
                                      <div className={styles.column}>
                                          <a href={link} className={styles.link}>
                                              <p className={styles.albumTitle}>
                                                  <TextShortener text={title as string} textLength={60} />
                                              </p>
                                          </a>
                                          <div>
                                              <Link
                                                  to={`/blog/${user && user.username}/`}
                                                  className={styles.usernameLink}
                                              >
                                                  <SVGIcon style={{ image: styles.userIcon }} src={IconUser} />
                                                  {user && displayedName(user)}
                                              </Link>
                                          </div>
                                      </div>
                                  </div>
                              );
                          })
                        : null}
                </LoadingWrapperView>
            )}
        </div>
    );
});

export default Styled(defaultStyles)(MostPopular);
